import React from "react";
import { Link } from "react-router-dom";
import Paginate from "../../../components/ui/paginate";
import { get_comments, create_comment } from "../../../service/api/blog";
import { useAppDispatch, useAppSelector } from "../../../store";
import { formatDate } from "../../../service/convert/date";
import { clearComments } from "../../../store/slices/blog";

type BlogCommentsProps = {
  postId: number;
};

const BlogComments: React.FC<BlogCommentsProps> = ({ postId }) => {
  const dispatch = useAppDispatch();
  const {
    comments,
    commentsLimit,
    commentsTotal,
    commentsLoading,
    commentSubmitting,
    commentsError,
  } = useAppSelector(({ blog }) => blog);
  const [offset, setOffset] = React.useState(0);
  const [body, setBody] = React.useState("");
  const isAuthenticated = Boolean(localStorage.getItem("@serial"));
  const currentPage =
    commentsLimit > 0 ? Math.floor(offset / commentsLimit) + 1 : 1;
  const lastPage =
    commentsLimit > 0
      ? Math.max(1, Math.ceil(commentsTotal / commentsLimit))
      : 1;

  React.useEffect(() => {
    setBody("");
    setOffset(0);
    dispatch(clearComments());
  }, [dispatch, postId]);

  React.useEffect(() => {
    void get_comments(dispatch, postId, commentsLimit, offset);
  }, [dispatch, postId, commentsLimit, offset]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedBody = body.trim();
    if (!trimmedBody || commentSubmitting) return;

    const created = await create_comment(dispatch, postId, {
      body: trimmedBody,
    });
    if (!created) return;

    setBody("");
    if (offset === 0) {
      await get_comments(dispatch, postId, commentsLimit, 0);
    } else {
      setOffset(0);
    }
  };

  return (
    <section className="mt-10 border-t border-gray-200 pt-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Comments ({commentsTotal})
      </h2>

      {commentsLoading ? (
        <p className="py-4 text-gray-500">Loading comments...</p>
      ) : comments.length ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <article key={comment.id} className="rounded-lg bg-gray-50 p-5">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold text-gray-900">
                  {comment.author_name || "Anonymous"}
                </h3>
                <time
                  className="text-sm text-gray-500"
                  dateTime={comment.created_at}
                >
                  {formatDate(new Date(comment.created_at))}
                </time>
              </div>
              <p className="whitespace-pre-wrap text-gray-700">
                {comment.body}
              </p>
            </article>
          ))}
        </div>
      ) : (
        <p className="py-4 text-gray-500">
          No comments yet. Be the first to comment.
        </p>
      )}

      {commentsError && (
        <p role="alert" className="mt-4 text-red-600">
          {commentsError}
        </p>
      )}

      <Paginate
        current_page={currentPage}
        last_page={lastPage}
        disabled={commentsLoading}
        onNext={() => setOffset(offset + commentsLimit)}
        onBack={() => setOffset(Math.max(0, offset - commentsLimit))}
      />

      <div className="mt-8">
        {isAuthenticated ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label
              htmlFor="blog-comment"
              className="block text-lg font-semibold text-gray-900"
            >
              Leave a comment
            </label>
            <textarea
              id="blog-comment"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Write your comment..."
              rows={4}
              required
              maxLength={5000}
              className="w-full rounded-lg border border-gray-300 bg-white p-3 text-gray-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
            <button
              type="submit"
              disabled={commentSubmitting || !body.trim()}
              className="rounded-lg bg-primary-500 px-5 py-3 font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {commentSubmitting ? "Posting..." : "Post comment"}
            </button>
          </form>
        ) : (
          <p className="text-center text-gray-600">
            <Link
              to="/login"
              className="font-semibold text-primary-600 hover:text-primary-700"
            >
              Sign in
            </Link>{" "}
            to leave a comment.
          </p>
        )}
      </div>
    </section>
  );
};

export default BlogComments;
